import { useEffect, useState, useCallback } from 'react';
import { SendDirectionsDialog } from './SendDirectionsDialog';

/**
 * FacilityDirectionsAdmin
 * Scheduler admin panel: gear → Settings → Directions
 *
 * Address comes from Pipeline (dbo.Facilities, read-only). Arrival
 * instructions + an optional map-link override are dock-owned
 * (dock.FacilityConfig, migration 113).
 */

interface Facility {
    FacilityCode: string;
    DisplayName:  string;
    Timezone:     string;
    WmsType:      string;
}

interface Directions {
    facilityCode:        string;
    displayName:         string;
    timezone:            string;
    address:             string | null;
    arrivalInstructions: string | null;
    mapLinkOverride:     string | null;
    effectiveMapLink:    string | null;
}

async function apiFetch<T>(path: string, token: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`/api/${path}`, {
        ...options, credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token, ...(options?.headers ?? {}) },
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error ?? `HTTP ${res.status}`); }
    if (res.status === 204) return undefined as unknown as T;
    return res.json();
}

/**
 * Normalize the /mgmt/facilities response into a Facility[] no matter how it's
 * shaped — bare array, or wrapped under a common key. Never throws; returns []
 * if it can't find an array, so a surprising shape degrades gracefully instead
 * of crashing the tab.
 */
function asFacilityArray(data: unknown): Facility[] {
    if (Array.isArray(data)) return data as Facility[];
    if (data && typeof data === 'object') {
        const o = data as Record<string, unknown>;
        for (const key of ['facilities', 'value', 'rows', 'items', 'data']) {
            if (Array.isArray(o[key])) return o[key] as Facility[];
        }
    }
    return [];
}

function buildMapLink(address: string | null): string | null {
    const t = (address ?? '').trim();
    return t ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(t)}` : null;
}

export default function FacilityDirectionsAdmin({ getToken }: { getToken: () => Promise<string> }) {
    const [facilities, setFacilities]     = useState<Facility[]>([]);
    const [selectedCode, setSelectedCode] = useState('');
    const [data, setData]                 = useState<Directions | null>(null);
    const [instructions, setInstructions] = useState('');
    const [override, setOverride]         = useState('');
    const [loading, setLoading]           = useState(false);
    const [saving, setSaving]             = useState(false);
    const [error, setError]               = useState<string | null>(null);
    const [saved, setSaved]               = useState(false);

    const [sendOpen, setSendOpen] = useState(false);

    useEffect(() => {
        getToken()
            .then(async t => {
                const [raw, me] = await Promise.all([
                    apiFetch<unknown>('mgmt/facilities', t),
                    apiFetch<{ defaultFacilityCode: string | null }>('me', t).catch(() => null),
                ]);
                const facs = asFacilityArray(raw);
                setFacilities(facs);
                if (facs.length) {
                    const codes = facs.map(f => f.FacilityCode);
                    const preferred = me?.defaultFacilityCode && codes.includes(me.defaultFacilityCode)
                        ? me.defaultFacilityCode
                        : facs[0].FacilityCode;
                    setSelectedCode(preferred);
                } else {
                    setError('No facilities returned.');
                }
            })
            .catch(e => setError(e instanceof Error ? e.message : 'Failed to load facilities.'));
    }, [getToken]);

    const load = useCallback(async (code: string) => {
        setLoading(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            const d = await apiFetch<Directions>(`mgmt/facilities/${code}/directions`, token);
            setData(d);
            setInstructions(d.arrivalInstructions ?? '');
            setOverride(d.mapLinkOverride ?? '');
        } catch (e) { setError(e instanceof Error ? e.message : 'Load failed'); }
        finally { setLoading(false); }
    }, [getToken]);

    useEffect(() => { if (selectedCode) load(selectedCode); }, [selectedCode, load]);

    const handleSave = async () => {
        if (!selectedCode) return;
        setSaving(true); setError(null); setSaved(false);
        try {
            const token = await getToken();
            await apiFetch(`mgmt/facilities/${selectedCode}/directions`, token, {
                method: 'PUT',
                body: JSON.stringify({
                    arrivalInstructions: instructions.trim() || null,
                    mapLinkOverride:     override.trim() || null,
                }),
            });
            // ⚠️ ORDER MATTERS, AND IT WAS WRONG (2026-08-17). setSaved(true)
            // used to run BEFORE this reload — and load() opens with
            // setSaved(false), so the "Directions saved." banner was raised and
            // wiped in the same tick. What you saw was a flash and no
            // confirmation, which is indistinguishable from a save that failed.
            // The banner has to be raised AFTER the refresh that clears it.
            await load(selectedCode);
            setSaved(true);
        } catch (e) { setError(e instanceof Error ? e.message : 'Save failed.'); }
        finally { setSaving(false); }
    };

    const previewLink = override.trim() || buildMapLink(data?.address ?? null);
    const selectedFac = facilities.find(f => f.FacilityCode === selectedCode);

    return (
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
            <div>
                <h1 className="text-xl font-semibold text-gray-900">Directions to Site</h1>
                <p className="text-sm text-gray-500 mt-0.5">Arrival instructions and a map link drivers see in the carrier portal. The street address comes from Pipeline.</p>
            </div>

            <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Facility</label>
                <select value={selectedCode} onChange={e => setSelectedCode(e.target.value)}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                    {facilities.map(f => (<option key={f.FacilityCode} value={f.FacilityCode}>{f.DisplayName} ({f.FacilityCode})</option>))}
                </select>
                {selectedFac && <span className="text-xs text-gray-400">{selectedFac.Timezone}</span>}
            </div>

            {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
            {saved && <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">Directions saved.</div>}

            {loading ? <div className="text-sm text-gray-400 animate-pulse py-8 text-center">Loading…</div> : data && (
                <div className="space-y-5">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Facility address (from Pipeline)</div>
                        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            {data.address?.trim()
                                ? data.address
                                : <span className="text-gray-400 italic">No address on file in Pipeline. Add it on the Pipeline facility record to enable the auto-generated map link, or set an override below.</span>}
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Arrival / gate instructions</label>
                        <textarea value={instructions} onChange={e => setInstructions(e.target.value)} rows={5} maxLength={2000}
                            placeholder="e.g. Enter at Gate 3 off Lake Powell Dr. Check in at the guard booth with your BOL. No overnight parking."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        <div className="text-[11px] text-gray-400 mt-1">{instructions.length}/2000 · Shown to drivers in the carrier portal.</div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Map link override <span className="text-gray-400 normal-case font-normal">(optional)</span>
                        </label>
                        <input type="url" value={override} onChange={e => setOverride(e.target.value)} maxLength={500}
                            placeholder="Leave blank to auto-generate from the address"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
                        <div className="text-[11px] text-gray-400 mt-1">Only set this if the auto-generated pin is wrong — e.g. the dock entrance differs from the billing address.</div>
                    </div>

                    <div className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
                        <div className="text-xs font-semibold text-blue-800 mb-1">Driver map link preview</div>
                        {previewLink
                            ? <a href={previewLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline break-all">{previewLink}</a>
                            : <span className="text-sm text-gray-400 italic">No address and no override — drivers won't see a map link.</span>}
                    </div>

                    <div className="flex items-center justify-between rounded-md border border-gray-200 p-4">
                        <div className="text-sm text-gray-600">Send these directions to a driver by email or text.</div>
                        <button type="button" onClick={() => setSendOpen(true)}
                            className="px-4 py-1.5 rounded-md bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 transition-colors">
                            Send directions
                        </button>
                    </div>

                    <div className="flex justify-end">
                        <button onClick={handleSave} disabled={saving}
                            className="px-5 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 transition-colors">
                            {saving ? 'Saving…' : 'Save Directions'}
                        </button>
                    </div>
                </div>
            )}
            {sendOpen && (
                <SendDirectionsDialog
                    open={true}
                    onClose={() => setSendOpen(false)}
                    facilityCode={selectedCode}
                    facilityLabel={selectedFac?.DisplayName}
                    getToken={getToken}
                />
            )}
        </div>
    );
}
